import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useState, useRef } from 'react';

const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [showHomeMenu, setShowHomeMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0 });
  const homeNavRef = useRef(null);
  const settingsNavRef = useRef(null);
  const settingsCloseTimeoutRef = useRef(null);

  const HOVER_CLOSE_DELAY_MS = 150;

  const handleMouseEnter = () => {
    if (homeNavRef.current) {
      const rect = homeNavRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.top });
    }
    setShowHomeMenu(true);
  };

  const handleSettingsMouseEnter = () => {
    if (settingsCloseTimeoutRef.current) {
      clearTimeout(settingsCloseTimeoutRef.current);
      settingsCloseTimeoutRef.current = null;
    }
    if (settingsNavRef.current) {
      const rect = settingsNavRef.current.getBoundingClientRect();
      setMenuPosition({ bottom: window.innerHeight - rect.top });
    }
    setShowSettingsMenu(true);
  };

  const handleSettingsMouseLeave = () => {
    settingsCloseTimeoutRef.current = setTimeout(() => {
      setShowSettingsMenu(false);
      settingsCloseTimeoutRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  const isActive = (path) => location.pathname === path;
  const isHomeActive = () => location.pathname === '/dashboard' || location.pathname === '/data-lake';
  const isSettingsActive = () => location.pathname === '/settings' || location.pathname.startsWith('/settings/');

  const dashboards = [
    { 
      name: 'API Testing Dashboard', 
      path: '/dashboard',
      description: 'Monitor and test your API endpoints',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
        </svg>
      )
    },
    { 
      name: 'Data Lake Testing', 
      path: '/data-lake',
      description: 'Comprehensive data lake validation and monitoring',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
        </svg>
      )
    }
  ];

  const settingsItems = [
    {
      name: 'General',
      path: '/settings',
      description: 'Environments, connections, exclusions, and more',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
        </svg>
      )
    },
    {
      name: 'Data Verification',
      path: '/settings/data-verification',
      description: 'Manage data verification rules',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
        </svg>
      )
    },
    {
      name: 'Schema Verification',
      path: '/settings/schema-verification',
      description: 'Compare schemas across sources (types, nullability, precision)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm1 4a1 1 0 00-1 1v8a1 1 0 001 1h12a1 1 0 001-1V9a1 1 0 00-1-1H4z" clipRule="evenodd"/>
        </svg>
      )
    }
  ];

  return (
    <div className="sidebar">
      {/* Logo */}
      <div className="sidebar-logo">
        <div className="logo-icon">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            {/* Hexagon shape */}
            <path d="M16 2L28 9V23L16 30L4 23V9L16 2Z" fill="url(#gradient1)" stroke="#58a6ff" strokeWidth="1.5"/>
            
            {/* API Signal waves */}
            <circle cx="16" cy="16" r="3" fill="#58a6ff"/>
            <circle cx="16" cy="16" r="6" stroke="#58a6ff" strokeWidth="1.5" opacity="0.6" fill="none"/>
            <circle cx="16" cy="16" r="9" stroke="#58a6ff" strokeWidth="1" opacity="0.3" fill="none"/>
            
            <defs>
              <linearGradient id="gradient1" x1="4" y1="2" x2="28" y2="30" gradientUnits="userSpaceOnUse">
                <stop stopColor="#1f6feb" stopOpacity="0.2"/>
                <stop offset="1" stopColor="#58a6ff" stopOpacity="0.1"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="sidebar-nav">
        <div 
          ref={homeNavRef}
          className="nav-item-wrapper"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowHomeMenu(false)}
        >
          <Link 
            to="/dashboard" 
            className={`nav-item ${isHomeActive() ? 'active' : ''}`}
            title="Dashboards"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
            </svg>
            <span className="nav-label">Dashboards</span>
          </Link>

          {showHomeMenu && (
            <div className="nav-submenu" style={{ top: `${menuPosition.top}px` }}>
              <div className="nav-submenu-header">
                <h3>DASHBOARDS</h3>
              </div>
              <div className="nav-submenu-items">
                {dashboards.map((dashboard) => (
                  <Link 
                    key={dashboard.path}
                    to={dashboard.path}
                    className={`nav-submenu-item ${location.pathname === dashboard.path ? 'active' : ''}`}
                  >
                    <div className="nav-submenu-icon">
                      {dashboard.icon}
                    </div>
                    <div className="nav-submenu-content">
                      <div className="nav-submenu-name">{dashboard.name}</div>
                      <div className="nav-submenu-description">{dashboard.description}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <Link 
          to="/endpoints" 
          className={`nav-item ${isActive('/endpoints') ? 'active' : ''}`}
          title="Endpoints"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
          </svg>
          <span className="nav-label">Endpoints</span>
        </Link>

        {/* Projects - commented out
        <Link 
          to="/projects" 
          className={`nav-item ${isActive('/projects') ? 'active' : ''}`}
          title="Projects"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
          </svg>
          <span className="nav-label">Projects</span>
        </Link>
        */}

        <Link 
          to="/collections" 
          className={`nav-item ${isActive('/collections') ? 'active' : ''}`}
          title="Collections"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z"/>
          </svg>
          <span className="nav-label">Collections</span>
        </Link>

        {/* Metrics - commented out
        <Link 
          to="/metrics" 
          className={`nav-item ${isActive('/metrics') ? 'active' : ''}`}
          title="Metrics"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/>
          </svg>
          <span className="nav-label">Metrics</span>
        </Link>
        */}

        <Link 
          to="/requests" 
          className={`nav-item ${isActive('/requests') ? 'active' : ''}`}
          title="Requests"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd"/>
          </svg>
          <span className="nav-label">Requests</span>
        </Link>

        <Link 
          to="/data-verification" 
          className={`nav-item ${isActive('/data-verification') ? 'active' : ''}`}
          title="Data Verification"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
          </svg>
          <span className="nav-label">Data Verification</span>
        </Link>

        <Link 
          to="/tables" 
          className={`nav-item ${isActive('/tables') ? 'active' : ''}`}
          title="Tables"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm1 4a1 1 0 00-1 1v8a1 1 0 001 1h12a1 1 0 001-1V9a1 1 0 00-1-1H4z" clipRule="evenodd"/>
          </svg>
          <span className="nav-label">Tables</span>
        </Link>

        <Link
          to="/schedules"
          className={`nav-item ${isActive('/schedules') ? 'active' : ''}`}
          title="Schedules"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" clipRule="evenodd"/>
          </svg>
          <span className="nav-label">Schedules</span>
        </Link>

        {user?.role === 'admin' && (
          <Link 
            to="/admin" 
            className={`nav-item ${isActive('/admin') ? 'active' : ''}`}
            title="Admin"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
            </svg>
            <span className="nav-label">Admin</span>
          </Link>
        )}
      </nav>

      {/* Bottom Navigation */}
      <div className="sidebar-bottom">
        <div
          ref={settingsNavRef}
          className="nav-item-wrapper"
          onMouseEnter={handleSettingsMouseEnter}
          onMouseLeave={handleSettingsMouseLeave}
        >
          <Link
            to="/settings"
            className={`nav-item ${isSettingsActive() ? 'active' : ''}`}
            title="Settings"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
            </svg>
            <span className="nav-label">Settings</span>
          </Link>

          {showSettingsMenu && (
            <div
              className="nav-submenu nav-submenu--bottom"
              style={{ bottom: `${menuPosition.bottom}px`, top: 'auto' }}
              onMouseEnter={handleSettingsMouseEnter}
              onMouseLeave={handleSettingsMouseLeave}
            >
              <div className="nav-submenu-header">
                <h3>SETTINGS</h3>
              </div>
              <div className="nav-submenu-items">
                {settingsItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-submenu-item ${location.pathname === item.path ? 'active' : ''}`}
                  >
                    <div className="nav-submenu-icon">
                      {item.icon}
                    </div>
                    <div className="nav-submenu-content">
                      <div className="nav-submenu-name">{item.name}</div>
                      <div className="nav-submenu-description">{item.description}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Help - commented out
        <Link 
          to="/help" 
          className={`nav-item ${isActive('/help') ? 'active' : ''}`}
          title="Help"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
          </svg>
          <span className="nav-label">Help</span>
        </Link>
        */}
      </div>
    </div>
  );
};

export default Sidebar;

